import { Color } from '@genial/design-tokens';
import { OrderEntityNotificationEntity, ProductAdditionalInfoEntity } from '@genial/services';
import { Utils } from '@genial/utils';
import * as html2pdf from 'html2pdf.js';

import type { FundOrderBuySuccessResponse } from '../../../routes/types';
import { mountAccount } from '../../../store/customer/normalize';
import { EQUITY_ORDER_TYPE_LABEL } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/Equity/ListItem';
import { ResponsesEquityOrderCreateResponseWithOrderType } from '../../CustomerDetail/components/Application/components/CustomerApplicationModal/products/EquityApplication/constants';

import { transformProductFieldsByProductType } from './transformer';
import { ElementsProps, OrderPdfTypes, ProductType } from './types';

export const messageError = '--';

export const normalizeProductType = (productType: string, data: OrderPdfTypes): ProductType => {
  const productTypeMap = {
    fundos: 'Fund',
    fund: 'Fund',
    variableincome: 'Equity',
    'renda variável': 'Equity',
    equity: 'Equity',
    'tesouro direto': 'DirectTreasury',
    directtreasury: 'DirectTreasury',
    'op. estruturadas': 'StructuredOperations',
    structuredoperations: 'StructuredOperations',
    fixedincome: 'FixedIncome',
    'renda fixa': 'FixedIncome',
  } as const;

  const rescueTypeMap = {
    fixedincome: 'FixedIncomeRescue',
    directtreasury: 'DirectTreasuryRescue',
    equity: 'EquityRescue',
    fund: 'FundRedemption',
  } as const;

  const normalizedType = productType.toLowerCase();
  const productTypeNormalized = productTypeMap[normalizedType];
  const productTypeNormalizedLowerCase = productTypeNormalized.toLowerCase();

  if (data.operationType === 'Venda' && rescueTypeMap[productTypeNormalizedLowerCase]) {
    return rescueTypeMap[productTypeNormalizedLowerCase];
  }

  return productTypeNormalized || 'FixedIncome';
};

export const defaultErrorMessage = 'Erro ao recuperar informação';

export const productItem = {
  FixedIncome: {
    title: 'RENDA FIXA',
    lineColor: Color['product.rf'],
  },
  FixedIncomeRescue: {
    title: 'RENDA FIXA',
    lineColor: Color['product.rf'],
  },
  Fund: {
    title: 'FUNDOS',
    lineColor: Color['product.fundos'],
  },
  FundRedemption: {
    title: 'FUNDOS',
    lineColor: Color['product.fundos'],
  },
  Equity: {
    title: 'RENDA VARIÁVEL',
    lineColor: Color['product.rendavariavelbuy'],
  },
  EquityRescue: {
    title: 'RENDA VARIÁVEL',
    lineColor: Color['product.rendavariavelsale'],
  },
  DirectTreasury: {
    title: 'TESOURO DIRETO',
    lineColor: Color['product.tesouro'],
  },
  DirectTreasuryRescue: {
    title: 'TESOURO DIRETO',
    lineColor: Color['product.tesouro'],
  },
  StructuredOperations: {
    title: 'OPERAÇÕES ESTRUTURADAS',
    lineColor: Color['product.estruturados'],
  },
};

export const getBackgroundColor = (productType: ProductType) => {
  return productItem[productType].lineColor;
};

export const createDefaultInfoElements = (value: ElementsProps) => {
  const elements = value
    .map(
      (item) => `
  <div class="flex-container">
    <strong class="order-label font-weight-regular line-height-lg font-size-md">
      ${item.label}
    </strong>
    <strong class="order-text full-width font-weight-regular line-height-lg font-size-md text-align-right">
      ${item.value}
    </strong>
  </div>`
    )
    .join('');

  return elements;
};

export const createMainFieldsElements = (productType: ProductType, data: OrderPdfTypes) => {
  const mappedMainFields = Object.values(transformProductFieldsByProductType[productType])
    .map((field) => {
      if (!field.label) {
        return;
      }

      if (field.label === 'Último preço') {
        return `
      <div class="details-row flex-container space-between">
        <span class="label-details-row font-size-md line-height-lg font-weight-regular">${
          field.label
        }</span>
        <div class="flex-container flex-column">
          <span class="value-details-row font-size-md line-height-lg font-weight-regular">${
            field.value.call(data, productType) ?? defaultErrorMessage
          }</span>
          <span class="font-size-sm line-height-sm font-weight-regular neutral-medium-color">${
            transformProductFieldsByProductType[productType].tradeDate?.value.call(
              data,
              productType
            ) ?? defaultErrorMessage
          }</span>
        </div>
      </div>
    `;
      }

      if (field.label === 'additionalInfo') {
        const additionalInfo = field.value.call(data, productType) as ProductAdditionalInfoEntity[];
        if (
          Array.isArray(additionalInfo) &&
          (additionalInfo as ProductAdditionalInfoEntity[]).length > 0
        ) {
          const dynamicData = additionalInfo?.slice(0, 2).map((info) => {
            return `
          <div class="details-row flex-container space-between">
            <span class="label-details-row font-size-md line-height-lg font-weight-regular">${
              info.description
            }</span>
            <span class="value-details-row font-size-md line-height-lg font-weight-regular">${Utils.Mask.Percentage(
              info.value || 0
            )}</span>
          </div>
        `;
          });

          return dynamicData.join('');
        }
      }

      return `
    <div class="details-row flex-container space-between">
      <span class="label-details-row font-size-md line-height-lg font-weight-regular">${
        field.label
      }</span>
      <span class="value-details-row font-size-md line-height-lg font-weight-regular">${
        field.value.call(data, productType) ?? defaultErrorMessage
      }</span>
    </div>
  `;
    })
    .join('');

  return mappedMainFields;
};

export const setupPDF = (htmlContent: string, filename: string) => {
  const opt = {
    margin: 10,
    filename,
    image: { type: 'pdf', quality: 1 },
    html2canvas: { scale: 1 },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
  };

  const element = document.createElement('div');
  element.innerHTML = htmlContent;

  html2pdf(element, opt);
};

export const parsePDFDataExport = (
  params: FundOrderBuySuccessResponse | Partial<FundOrderBuySuccessResponse>
) => {
  const operationType = params?.operationTypeIsSale ? 'Venda' : 'Compra';
  const orderName =
    params?.order?.orderName && params?.order?.orderHash
      ? `${params?.order.orderName} - ${params?.order.orderHash}`
      : messageError;

  const operationName =
    params?.offer?.fantasyName && params?.offer?.asset
      ? `${params?.offer?.fantasyName} ${params?.offer?.asset}`
      : messageError;

  const minimumApplication = params?.offer?.minimumApplication
    ? Utils.Mask.Money(params?.offer?.minimumApplication)
    : messageError;

  const estimatedNetValue =
    params?.order?.orderAmount !== undefined &&
    params?.order?.orderAmount !== null &&
    params?.offer?.expectedIR !== undefined &&
    params?.offer.expectedIR !== null
      ? Utils.Mask.Money(params.order.orderAmount - params.offer.expectedIR)
      : messageError;

  const requestedOfferDetail = params?.requestedOfferDetail || params.order?.requestedOfferDetail;

  const dateQuotation = params?.fullProductItem?.dateQuotation;
  const requestedDateQuotation = params?.offer?.requestedDateQuotation;

  const quotationDate =
    dateQuotation?.quotationDate ||
    requestedDateQuotation?.quotationDate ||
    params?.offer?.quotationDate;
  const liquidationDate =
    dateQuotation?.liquidityDate ||
    requestedDateQuotation?.liquidityDate ||
    params?.offer?.liquidationDate;

  const equityOrderType =
    params?.fullProductItem?.orderType ||
    (params as unknown as ResponsesEquityOrderCreateResponseWithOrderType)?.orderType;

  const custodyAccount = mountAccount(
    params?.fullCustomerItem?.pnpAccountNumber || 0,
    params?.fullCustomerItem?.pnpAccountDigit
  );

  const equityPdfOrderTypeDomain = equityOrderType === 'Market' ? 'Market' : 'Limit';

  const data = {
    orderName,
    applicationIn: params?.offer?.applicationIn || messageError,
    strategy: params?.offer?.strategy || messageError,
    orderDate: params?.order?.orderDate,
    orderAmount: params?.order?.orderAmount,
    additionalInfo: params?.offer?.additionalInfo || [],
    minimumApplication,
    operationName,
    productDisplay:
      params?.offer?.productDisplayNormalized ||
      params?.offer?.productNameNormalized ||
      params?.offer?.fundName ||
      params?.offer?.issuerFullName,
    accountNumber: params?.customer?.accountNumber,
    riskRatingName: params?.offer?.riskRatingName || params?.offer?.suitabilityProductRisk?.risk,
    maturityDate: params?.offer?.maturityDate,
    liquidityIndicator: params?.offer?.liquidityIndicatorNormalized,
    feesPayment: params?.offer?.feesPaymentNormalized,
    qualifiedInvestor: params?.offer?.qualifiedInvestor,
    riskRating: params?.offer?.riskRating,
    redemptionDescription: params?.offer?.redemptionDescription,
    administrationFee: params?.offer?.administrationFee,
    profitabilityTwelveMonthsAdjusted: params?.offer?.profitabilityTwelveMonthsAdjusted?.toString(),
    quotationRedemptionTermFormatted: params?.offer?.quotationRedemptionTermFormatted,
    productName: params?.offer?.productName || messageError,
    symbol: params?.offer?.symbol || messageError,
    lastPrice: params?.offer?.lastPrice || messageError,
    priceChangeTax: params?.offer?.priceChangeTax || 0,
    orderQuantity: params?.order?.orderQuantity || params?.order?.quantity || messageError,
    tradeDate: params?.offer?.tradeDate || messageError,
    orderPrice: params?.order?.orderPrice || 0,
    orderType: EQUITY_ORDER_TYPE_LABEL[equityPdfOrderTypeDomain],
    titleName: params?.offer?.titleName ? params?.offer?.titleName : messageError,
    settlementDate: params?.offer?.settlementDate ? params?.offer?.settlementDate : messageError,
    operationType,
    redemptionDate: params?.order?.redemptionDate || messageError,
    netFinancialValue: params?.offer?.netFinancialValue || messageError,
    ir: params?.offer?.ir || messageError,
    estimatedNetValue,
    custodyAccount,
    quotationDate: quotationDate
      ? Utils.Mask.DateFormat(quotationDate, 'dd/MM/yyyy')
      : messageError,
    liquidationDate: liquidationDate
      ? Utils.Mask.DateFormat(liquidationDate, 'dd/MM/yyyy')
      : messageError,
    customerFeeAmount: requestedOfferDetail?.customerFeeAmount
      ? Utils.Mask.Percentage(requestedOfferDetail.customerFeeAmount)
      : messageError,
    customerPuAmount: requestedOfferDetail?.customerPuAmount
      ? Utils.Mask.Money(requestedOfferDetail.customerPuAmount, { maximumFractionDigits: 8 })
      : messageError,
  } as unknown as OrderPdfTypes;

  return data;
};

export const notificationChannelsData = (
  params?: FundOrderBuySuccessResponse | { order: { notification: OrderEntityNotificationEntity } }
) =>
  params?.order?.notification?.notificationChannels
    ?.map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' - ');

export const updateInfosToExport = (params: FundOrderBuySuccessResponse) => {
  const profitabilityToMaturityNormalized = params?.fullProductItem?.orderFee?.profitability;

  return {
    ...params,
    offer: {
      ...params?.offer,
      profitabilityToMaturityNormalized,
    },
  };
};
